import React from 'react';
import { Box, Card, CardActionArea, CardContent, CardMedia, Grid, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DescriptionIcon from '@mui/icons-material/Description';
import GavelIcon from '@mui/icons-material/Gavel';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PaymentsIcon from '@mui/icons-material/Payments';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import WorkIcon from '@mui/icons-material/Work';
import GroupsIcon from '@mui/icons-material/Groups';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import CampaignIcon from '@mui/icons-material/Campaign';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EventIcon from '@mui/icons-material/Event';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import SchoolIcon from '@mui/icons-material/School';
import PhoneIcon from '@mui/icons-material/Phone';
import PlaceIcon from '@mui/icons-material/Place';
import LinkIcon from '@mui/icons-material/Link';
import { SvgIconComponent } from '@mui/icons-material';
import LinkButtonCard from '../components/shared/link-button-card';
import ImageGallery from '../components/home/image-gallery';
import { RichTextContent } from '../custom-page/rich-text-content';
import { HomeSectionTitle, PageSection } from './home.styles';
import BodyBanner from './sections/body-banner';
import SectionNewsHighlights from './sections/newsHighlights';
import SectionPublicationsTabs from './sections/section-publications-tabs';
import { useMenuLinkNavigation } from '../hooks/useMenuLinkNavigation';
import { useRoutedNavigation } from '../hooks/useRoutedNavigation';
import { youtubeEmbedUrl } from '../../utils/youtube';
import { IBannerReponse } from '../../model/base/response/banner-response';
import {
  HomeSectionType,
  IPortalHomeSectionItemResponse,
  IPortalHomeSectionResponse,
} from '../../model/base/response/portal-home-response';

/** Quantidade padrão de notícias quando a seção não define (mesma da home padrão). */
export const DEFAULT_NEWS_QUANTITY = 4;

// Ícones do Acesso Rápido: mesmas chaves gravadas pelo APM (ACESSO_RAPIDO_ICONS)
const ACCESS_ICONS: Record<string, SvgIconComponent> = {
  description: DescriptionIcon,
  gavel: GavelIcon,
  account_balance: AccountBalanceIcon,
  payments: PaymentsIcon,
  receipt_long: ReceiptLongIcon,
  shopping_cart: ShoppingCartIcon,
  work: WorkIcon,
  groups: GroupsIcon,
  support_agent: SupportAgentIcon,
  campaign: CampaignIcon,
  visibility: VisibilityIcon,
  event: EventIcon,
  newspaper: NewspaperIcon,
  menu_book: MenuBookIcon,
  health_and_safety: HealthAndSafetyIcon,
  school: SchoolIcon,
  phone: PhoneIcon,
  place: PlaceIcon,
  link: LinkIcon,
};

function Section({ title, subtitle, children }: { title?: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <PageSection>
      {title && (
        <HomeSectionTitle>
          {title} <hr />
        </HomeSectionTitle>
      )}
      {subtitle && (
        <p style={{ fontSize: '16px', color: '#666', marginBottom: '32px' }}>
          {subtitle}
        </p>
      )}
      {children}
    </PageSection>
  );
}

// Banner de corpo: posicionado entre as seções, com layout próprio (o carrossel do topo é renderizado pela HomeComponent)
function BannersSection({ section }: { section: IPortalHomeSectionResponse }) {
  const sectionBanners: IBannerReponse[] = (section.items || [])
    .filter((item) => !!item.imageUrl)
    .map((item) => ({
      id: item.id,
      title: item.title,
      urlImage: item.imageUrl,
      nomImgBanner: item.image,
      url: item.url,
      openInNewTab: item.openInNewTab,
    }));

  if (sectionBanners.length === 0) return null;

  return <BodyBanner banners={sectionBanners} />;
}

function FreeTextSection({ section }: { section: IPortalHomeSectionResponse }) {
  if (!section.content) return null;

  return (
    <Section title={section.title} subtitle={section.subtitle}>
      <RichTextContent html={section.content} />
    </Section>
  );
}

/** Destino do atalho para o LinkButtonCard: externo (ou interno em nova aba) com o endereço já resolvido. */
function accessLink(url: string, openInNewTab: boolean, buildPath: (path: string) => string) {
  if (/^https?:\/\//i.test(url)) return { href: url, external: true };
  // Link externo cadastrado sem protocolo (ex.: "www.site.com.br"); slugs e rotas do portal nunca têm ponto
  if (/^[\w-]+(\.[\w-]+)+(\/|$)/i.test(url)) return { href: `https://${url}`, external: true };

  const path = url.startsWith('/') ? url : `/${url}`;
  return openInNewTab ? { href: buildPath(path), external: true } : { href: path, external: false };
}

function QuickAccessSection({ section }: { section: IPortalHomeSectionResponse }) {
  const { buildPath } = useRoutedNavigation();
  const items = (section.items || []).filter((item) => !!item.url);
  if (!items.length) return null;

  return (
    <Section title={section.title || 'Acesso Rápido'} subtitle={section.subtitle}>
      <Grid container spacing={3}>
        {items.map((item) => {
          const Icon = (item.icon && ACCESS_ICONS[item.icon]) || LinkIcon;

          return (
            <Grid key={item.id} item xs={6} sm={4} md={2.4}>
              <LinkButtonCard
                title={item.title || ''}
                description={item.description}
                icon={<Icon />}
                {...accessLink(item.url!, item.openInNewTab, buildPath)}
              />
            </Grid>
          );
        })}
      </Grid>
    </Section>
  );
}

function HighlightCard({ item }: { item: IPortalHomeSectionItemResponse }) {
  const openLink = useMenuLinkNavigation();

  const body = (
    <>
      {item.imageUrl && (
        <CardMedia component="img" image={item.imageUrl} alt={item.title || ''} sx={{ height: 200, objectFit: 'cover' }} />
      )}
      <CardContent>
        <Typography variant="h6" component="h3" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
          {item.title}
        </Typography>
        {item.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-line' }}>
            {item.description}
          </Typography>
        )}
        {item.url && (
          <Typography
            variant="caption"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1.5, color: 'primary.main', fontWeight: 600, textTransform: 'uppercase' }}
          >
            Saiba mais <ArrowForwardIcon sx={{ fontSize: '1rem' }} />
          </Typography>
        )}
      </CardContent>
    </>
  );

  return (
    <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
      {item.url ? (
        <CardActionArea
          sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'flex-start' }}
          onClick={() => openLink(item.url, item.openInNewTab)}
        >
          {body}
        </CardActionArea>
      ) : body}
    </Card>
  );
}

function HighlightCardsSection({ section }: { section: IPortalHomeSectionResponse }) {
  const items = section.items || [];
  if (!items.length) return null;

  const md = items.length >= 3 ? 4 : 12 / items.length;

  return (
    <Section title={section.title || 'Destaques'} subtitle={section.subtitle}>
      <Grid container spacing={3}>
        {items.map((item) => (
          <Grid item xs={12} sm={6} md={md} key={item.id}>
            <HighlightCard item={item} />
          </Grid>
        ))}
      </Grid>
    </Section>
  );
}

function VideosSection({ section }: { section: IPortalHomeSectionResponse }) {
  const videos = (section.items || [])
    .map((item) => ({ item, embedUrl: youtubeEmbedUrl(item.url) }))
    .filter((video): video is { item: IPortalHomeSectionItemResponse; embedUrl: string } => !!video.embedUrl);

  if (!videos.length) return null;

  const md = videos.length >= 3 ? 4 : 12 / videos.length;

  return (
    <Section title={section.title || 'Vídeos'} subtitle={section.subtitle}>
      <Grid container spacing={3}>
        {videos.map(({ item, embedUrl }) => (
          <Grid item xs={12} sm={6} md={md} key={item.id}>
            <Card elevation={2} sx={{ borderRadius: 2 }}>
              <Box sx={{ position: 'relative', pt: '56.25%' }}>
                <Box
                  component="iframe"
                  src={embedUrl}
                  title={item.title || 'Vídeo'}
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
                />
              </Box>
              {item.title && (
                <Typography fontWeight={600} sx={{ p: 2 }}>
                  {item.title}
                </Typography>
              )}
            </Card>
          </Grid>
        ))}
      </Grid>
    </Section>
  );
}

function ImageGallerySection({ section }: { section: IPortalHomeSectionResponse }) {
  if (!section.items?.some((item) => !!item.imageUrl)) return null;

  return (
    <Section title={section.title || 'Galeria de Imagens'} subtitle={section.subtitle}>
      <ImageGallery items={section.items} configuration={section.configuration} />
    </Section>
  );
}

function HomeSection({ section }: { section: IPortalHomeSectionResponse }) {
  switch (section.type) {
    case HomeSectionType.Banners:
      return <BannersSection section={section} />;
    case HomeSectionType.DiarioOficial:
      return <SectionPublicationsTabs title={section.title} subtitle={section.subtitle} />;
    case HomeSectionType.Noticias:
      return <SectionNewsHighlights title={section.title} quantity={section.itemsQuantity || DEFAULT_NEWS_QUANTITY} />;
    case HomeSectionType.TextoLivre:
      return <FreeTextSection section={section} />;
    case HomeSectionType.AcessoRapido:
      return <QuickAccessSection section={section} />;
    case HomeSectionType.CardDestaque:
      return <HighlightCardsSection section={section} />;
    case HomeSectionType.Videos:
      return <VideosSection section={section} />;
    case HomeSectionType.GaleriaImagens:
      return <ImageGallerySection section={section} />;
    default:
      return null;
  }
}

/** Home montada pela customização do APM, na ordem e com as seções visíveis que a API devolve. */
export function CustomHome({ sections }: { sections: IPortalHomeSectionResponse[] }) {
  return (
    <>
      {sections.map((section) => (
        <HomeSection key={section.id} section={section} />
      ))}
    </>
  );
}
